import { useMutation } from "@apollo/client";
import { HAS_USER_OPENED_CV_AI_HELPER, MAX_FILE_SIZE } from "@constants/global";
import AILoader from "components/AILoader";
import { ModalButtons } from "components/ui/v2/modal-buttons";
import { Sparkles } from "lucide-react";
import AiRobot from "components/Lottie/AiRobot";
import SuccessView from "components/SuccessView";
import UploadFileLottie from "components/_Pages/NoResult/UploadFileLottie";
import { useDrawer } from "context/drawer/drawer.provider";
import { gqlUser } from "gql";
import useTranslation from "next-translate/useTranslation";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Loader, Uploader } from "rsuite";
import { FileType } from "rsuite/esm/Uploader/Uploader";
import { UploadPair } from "types/Multimedia.type";
import useSound from "use-sound";
import { getQueryOperator } from "utils/helpers";
import { readFile } from "utils/files";

const AIHelperCV = () => {
  const emptyUploadPair: UploadPair = { name: "", base64: "" };
  const { t } = useTranslation("common");
  const [playComplete] = useSound("/sounds/complete.mp3", { volume: 0.25 });
  const [creating, setCreating] = useState<boolean>(false);
  const [aiReady, setAIReady] = useState<boolean>(false);
  const [file, setFile] = useState<UploadPair>(emptyUploadPair);
  const [loading] = useState(false);
  const [fileList, setFileList] = useState<FileType[]>([]);

  const { closeDrawer } = useDrawer();

  const [playAi] = useSound("/sounds/ai-button.mp3", { volume: 0.25 });

  const [createCvWithAI] = useMutation(gqlUser.mutations.CREATE_CV_WITH_AI, {
    awaitRefetchQueries: true,
    refetchQueries: [
      getQueryOperator(gqlUser.queries.GET_FULL_USER),
      getQueryOperator(gqlUser.queries.GET_USER_AI_CV_INFO)
    ],
    onCompleted() {
      toast.success(t("CV creado con éxito"));
      setCreating(false);
      playComplete();
      setAIReady(true);

      if (!localStorage.getItem(HAS_USER_OPENED_CV_AI_HELPER)) {
        localStorage.setItem(HAS_USER_OPENED_CV_AI_HELPER, "true");
      }
    },
    onError(e) {
      toast.error(e.message);
    }
  });

  useEffect(() => {
    if (!localStorage.getItem(HAS_USER_OPENED_CV_AI_HELPER)) {
      playAi();
    }
  }, []);

  const handleFileChange = async (fileList: Array<FileType>) => {
    if (fileList.length > 0) {
      // * check if blob file length is greater than max file size
      if (fileList[0]?.blobFile && fileList[0].blobFile.size > MAX_FILE_SIZE) {
        toast.error(
          `${t("El archivo es muy pesado. El tamaño máximo es de")} ${
            MAX_FILE_SIZE / 1000000
          } MB.`
        );
        return;
      }

      if (!fileList[0]?.blobFile) {
        toast.error(t("Error al subir archivo"));
        return;
      }

      try {
        const dataUrl = await readFile(fileList[0].blobFile);
        setFileList(fileList);
        setFile({
          name: fileList[0].blobFile.name,
          base64: dataUrl
        });
      } catch {
        toast.error(t("Error al subir archivo"));
      }
    }
  };

  const handleCreate = () => {
    setCreating(true);
  };

  const sendReq = () => {
    createCvWithAI({
      variables: {
        createCvWithAiInput: {
          cvB64: file.base64
        }
      }
    });
  };

  const handleCancel = () => {
    setCreating(false);
    setAIReady(false);
  };

  const handleRemove = () => {
    setFileList([]);
    setFile(emptyUploadPair);
  };

  if (creating && !aiReady) {
    return (
      <AILoader
        title={t("Creando tu Curriculum")}
        execReq={sendReq}
        cancelReq={handleCancel}
        body={
          <div className="my-4 flex flex-col items-center justify-center gap-2">
            <span className="text-center">
              {t(
                "Estoy creando tu CV a partir de tus habilidades, experiencia y el CV en formato PDF que compartiste"
              )}
            </span>
          </div>
        }
      />
    );
  }

  if (!creating && aiReady) {
    return (
      <SuccessView
        text={t("Tu resume ha sido creado con éxito")}
        btnClick={closeDrawer}
        btnLabel={t("Cerrar")}
      />
    );
  }

  return (
    <div className="flex flex-col bg-white text-slate-900 dark:bg-zinc-950 dark:text-white">
      <div className="flex flex-col">
        <span className="place-self-start">
          <AiRobot size={40} height={40} />
        </span>
        <span className="mt-4 text-left text-lg font-semibold text-slate-900 dark:text-white">
          <span>{t("Crea tu Resume con AI")}</span>
          <Sparkles className="h-4 w-4 ml-2" />
        </span>
        <p className="mb-4 text-left text-base text-slate-700 dark:text-zinc-300">
          {t(
            "Soy un asistente, te puedo ayudar a crear tu CV a partir de tus habilidades, experiencia y algún CV en formato PDF que puedas tener"
          )}
        </p>
      </div>

      <div className="flex w-full flex-col gap-2">
        <div className="rounded-2xl border border-slate-200/70 bg-slate-50/50 p-4 dark:border-zinc-800 dark:bg-zinc-900/50">
          <Uploader
            action=""
            draggable
            accept="application/pdf"
            maxPreviewFileSize={MAX_FILE_SIZE}
            fileListVisible
            removable
            onChange={handleFileChange}
            onRemove={handleRemove}
            fileList={fileList}
            autoUpload={false}
          >
            <div
              className="cursor-pointer rounded-xl"
              style={{
                width: "100%",
                height: 180,
                alignContent: "center",
                justifyContent: "center",
                display: "flex"
              }}
            >
              {
                loading ? (
                  <Loader size="lg" />
                ) : (
                  <UploadFileLottie
                    size={150}
                    message={t("Arrastra y suelta tus archivos aquí")}
                  />
                ) // * Icono de archivo
              }
            </div>
          </Uploader>
        </div>
      </div>

      <ModalButtons
        loading={creating}
        onCancel={closeDrawer}
        disabled={!file.base64 && fileList.length === 0}
        submitLabel={t("Crear")}
        onSubmit={handleCreate}
      />
    </div>
  );
};

export default AIHelperCV;
